import {
  getGetWorkspaceInvitationsApiV1QueryKey,
  useCancelInvitationApiV1,
  useGetWorkspaceInvitationsApiV1Suspense,
  useGetWorkspaceMembersApiV1Suspense,
} from "@/api/__generated__/api.schema";
import {
  WorkspaceInvitationStatus,
  type WorkspaceMemberListItem,
  WorkspaceMemberRole,
} from "@/api/__generated__/models";
import { Profile } from "@/components/profile";
import { Allowed } from "@/components/role/allowed";
import { WorkspaceMemberRoleLabel } from "@/components/role/workspace-member-role-label";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { WorkspaceMemberManageForm } from "@/routes/settings/workspace/components/members/workspace-member-manamge-form";
import { getColorFromIndex } from "@/utils/color-utils";
import { distanceToNow } from "@/utils/time";
import { useToastMessage } from "@backpac/monica";
import {
  Button,
  FullScreenOverlayLayout,
  Loading,
  SheetLayout,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Typography,
  useFullScreenOverlay,
  useModal,
} from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { Suspense, useCallback, useState } from "react";
import { InviteToWorkspaceForm } from "./members/invite-to-workspace-form";

export function WorkspaceMembersSetting() {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: members },
    },
  } = useGetWorkspaceMembersApiV1Suspense(workspaceId);

  const workspaceMembers = members?.sort((a, b) => {
    const rolePriority = {
      [WorkspaceMemberRole.OWNER]: 1,
      [WorkspaceMemberRole.ADMIN]: 2,
      [WorkspaceMemberRole.MEMBER]: 3,
    };

    const roleDiff = rolePriority[a.role] - rolePriority[b.role];
    if (roleDiff !== 0) return roleDiff;

    return a.name.localeCompare(b.name);
  });

  const {
    data: {
      data: { items: invitations },
    },
  } = useGetWorkspaceInvitationsApiV1Suspense(workspaceId);
  const pendingInvitations = invitations
    .filter((invitation) => invitation.status === WorkspaceInvitationStatus.PENDING)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  return (
    <div>
      <Table
        border={false}
        head={
          <div className="flex items-baseline justify-between px-6 mb-6">
            <Typography size="lg" color="black" font="unbounded">
              Members
            </Typography>
            <Allowed.Admin>
              <InviteMemberButton />
            </Allowed.Admin>
          </div>
        }
      >
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-6">Name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Manage Channels</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>Last active</TableHead>
            <TableHead />
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {workspaceMembers?.map((member) => (
            <TableRow key={member.userId + member.role + member.permittedChannelsCount}>
              <TableCell className="pl-6">
                <Profile
                  name={member.name}
                  description={member.email}
                  profileImageUrl={member.thumbnailUrl}
                  size="md"
                />
              </TableCell>
              <TableCell>
                <WorkspaceMemberRoleLabel role={member.role} />
              </TableCell>
              <TableCell>
                <CountOfManageChannels role={member.role} count={member.permittedChannelsCount} />
              </TableCell>
              <TableCell className="whitespace-nowrap">{distanceToNow(member.joinedAt)}</TableCell>
              <TableCell className="whitespace-nowrap">
                {member?.lastActiveAt != null ? distanceToNow(member.lastActiveAt) : ""}
              </TableCell>
              <TableCell>
                {member.role !== WorkspaceMemberRole.OWNER ? (
                  <Allowed.Admin>
                    <ManageMemberControls member={member} />
                  </Allowed.Admin>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
          {pendingInvitations.map((invitation, index) => (
            <TableRow key={invitation.id}>
              <TableCell className="pl-6">
                <Profile
                  size="md"
                  name={invitation.email}
                  description="Invite pending"
                  fallbackClassName={cn(getColorFromIndex(index), "text-white text-sm font-semibold")}
                />
              </TableCell>
              <TableCell>
                <WorkspaceMemberRoleLabel role={invitation.role} />
              </TableCell>
              <TableCell>
                <CountOfManageChannels role={invitation.role} count={invitation.channelIds.length} />
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell>
                <Allowed.Admin>
                  <InivtationControls invitationId={invitation.id} />
                </Allowed.Admin>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function InviteMemberButton() {
  const isMobile = useIsMobile();
  const { open, close } = useModal();
  const { open: openFullScreenOverlay, close: closeFullScreenOverlay } = useFullScreenOverlay();
  const openInviteModal = useCallback(() => {
    if (isMobile) {
      openFullScreenOverlay({
        title: "Invite to workspace",
        children: (
          <Suspense fallback={<Loading className="min-h-[400px]" />}>
            <div className="py-6 px-4">
              <InviteToWorkspaceForm
                defaultValues={{
                  role: WorkspaceMemberRole.ADMIN,
                  emails: [{ value: "" }],
                }}
                onClose={closeFullScreenOverlay}
              />
            </div>
          </Suspense>
        ),
      });
    } else {
      open({
        title: "Invite to workspace",
        className: "max-w-md",
        children: (
          <Suspense fallback={<Loading className="min-h-[240px]" />}>
            <InviteToWorkspaceForm
              defaultValues={{ role: WorkspaceMemberRole.ADMIN, emails: [{ value: "" }] }}
              onClose={close}
            />
          </Suspense>
        ),
      });
    }
  }, [open, close, openFullScreenOverlay, closeFullScreenOverlay, isMobile]);

  return (
    <Button onClick={openInviteModal} leftAddon={<i className="inline-block i-bp-plus size-3" />}>
      Invite
    </Button>
  );
}

function CountOfManageChannels({ role, count }: { role: WorkspaceMemberRole; count: number | undefined }) {
  if (role === WorkspaceMemberRole.ADMIN || role === WorkspaceMemberRole.OWNER) {
    // NOTE: count value is undefined when the role is ADMIN or OWNER for now
    return "All";
  }

  return count;
}

function ManageMemberControls({ member }: { member: WorkspaceMemberListItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <>
      <Button variant="ghost" aria-label="Member options" className="p-2" onClick={() => setIsOpen(true)}>
        <i className="inline-block i-bp-manage size-4" />
      </Button>

      {isMobile ? (
        <FullScreenOverlayLayout open={isOpen} onOpenChange={setIsOpen} title="Manage">
          <Suspense fallback={<Loading className="min-h-[400px]" />}>
            <WorkspaceMemberManageForm memberId={member.userId} onClose={() => setIsOpen(false)} />
          </Suspense>
        </FullScreenOverlayLayout>
      ) : (
        <SheetLayout open={isOpen} onOpenChange={setIsOpen} title="Manage">
          <Suspense fallback={<Loading className="min-h-[400px]" />}>
            <WorkspaceMemberManageForm memberId={member.userId} onClose={() => setIsOpen(false)} />
          </Suspense>
        </SheetLayout>
      )}
    </>
  );
}

function InivtationControls({ invitationId }: { invitationId: string }) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const { info } = useToastMessage();
  const { mutate: removeInvitation, isPending } = useCancelInvitationApiV1({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getGetWorkspaceInvitationsApiV1QueryKey(workspaceId) });
        info("Invitation removed");
      },
    },
  });

  return (
    <Button
      variant="ghost"
      aria-label="Member options"
      className="p-2"
      onClick={() => removeInvitation({ workspaceId, invitationId })}
      disabled={isPending}
    >
      <i className="inline-block i-bp-trash size-4" />
    </Button>
  );
}
